import React from "react"
import { cn } from "@/lib/utils"

/**
 * FormField - Wrapper dla pól formularza w stylu Radix Themes
 * 
 * Funkcje:
 * - Grupuje Label + Input + FieldError
 * - Spacing z 8px scale
 * - Accessibility (a11y)
 * 
 * @example
 * ```tsx
 * <FormField>
 *   <Label htmlFor="email" required>Email</Label>
 *   <Input id="email" type="email" error={!!errors.email} />
 *   {errors.email && <FieldError>{errors.email}</FieldError>}
 * </FormField>
 * ```
 */

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {}

const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-[var(--space-2)]",
        "mb-[var(--space-6)]",
        className
      )}
      {...props}
    />
  )
)
FormField.displayName = "FormField"

/**
 * FieldError - Komunikat błędu pola formularza
 */
export interface FieldErrorProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const FieldError = React.forwardRef<HTMLParagraphElement, FieldErrorProps>(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      role="alert"
      aria-live="polite"
      className={cn(
        "text-[var(--font-size-2)]",
        "font-[var(--font-weight-regular)]",
        "leading-[var(--line-height-3)]",
        "text-[var(--danger-11)]",
        "mt-[var(--space-1)]",
        className
      )}
      {...props}
    >
      {children}
    </p>
  )
)
FieldError.displayName = "FieldError"

/**
 * FieldHint - Pomocniczy tekst dla pola formularza
 */
export interface FieldHintProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const FieldHint = React.forwardRef<HTMLParagraphElement, FieldHintProps>(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        "text-[var(--font-size-2)]",
        "font-[var(--font-weight-regular)]",
        "leading-[var(--line-height-3)]",
        "text-[var(--text-white-65)]",
        "mt-[var(--space-1)]",
        className
      )}
      {...props}
    >
      {children}
    </p>
  )
)
FieldHint.displayName = "FieldHint"

/**
 * FormSection - Sekcja formularza z nagłówkiem
 */
export interface FormSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  description?: string
}

const FormSection = React.forwardRef<HTMLDivElement, FormSectionProps>(
  ({ className, title, description, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "mb-[var(--space-10)]",
        className
      )}
      {...props}
    >
      {(title || description) && (
        <div className="mb-[var(--space-6)]">
          {title && (
            <h3 className={cn(
              "text-[var(--font-size-6)]",
              "font-[var(--font-weight-bold)]",
              "leading-[var(--line-height-2)]",
              "text-[var(--text-white-95)]",
              "mb-[var(--space-2)]"
            )}>
              {title}
            </h3>
          )}
          {description && (
            <p className={cn(
              "text-[var(--font-size-2)]",
              "leading-[var(--line-height-4)]",
              "text-[var(--text-white-70)]"
            )}>
              {description}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  )
)
FormSection.displayName = "FormSection"

export { FormField, FieldError, FieldHint, FormSection }

